import { createContext, useEffect, useState, ReactNode } from 'react'
import { apiRequest, ApiException } from '../../../shared/hooks/useApi'

export interface User {
  id: number
  email: string
  name?: string
}

export interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (email: string) => Promise<void>
  signOut: () => Promise<void>
  error: string | null
  clearError: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const clearError = () => setError(null)

  // Check for existing session on mount
  useEffect(() => {
    checkSession()
  }, [])

  const checkSession = async () => {
    try {
      setIsLoading(true)
      const sessionData = await apiRequest<{ user: User }>('/auth/session')
      setUser(sessionData.user)
    } catch (error) {
      // No valid session, user needs to authenticate
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const signIn = async (email: string) => {
    try {
      setIsLoading(true)
      setError(null)
      
      await apiRequest('/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email })
      })
      
      // Success - magic link sent
    } catch (error) {
      if (error instanceof ApiException) {
        setError(error.message)
      } else {
        setError('Failed to send magic link. Please try again.')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setIsLoading(true)
      await apiRequest('/auth/signout', { method: 'POST' })
      setUser(null)
    } catch (error) {
      // Even if signout fails on server, clear local state
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signOut,
    error,
    clearError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

